export type StoreThemePreset = 'classic' | 'urban' | 'bistro' | 'fresh';

export type StoreBranding = {
  name?: string;
  logoUrl?: string;
  coverImageUrl?: string;
  tagline?: string;
  primaryColor: string;
  accentColor: string;
  themePreset: StoreThemePreset;
};

const DEFAULT_PRIMARY = '#ff6b00';
const DEFAULT_ACCENT = '#ffb26b';
const DEFAULT_PRESET: StoreThemePreset = 'classic';

const isHexColor = (value: unknown): value is string =>
  typeof value === 'string' && /^#([0-9a-fA-F]{6})$/.test(value.trim());

const normalizePreset = (value: unknown): StoreThemePreset => {
  if (value === 'urban' || value === 'bistro' || value === 'fresh' || value === 'classic') {
    return value;
  }

  return DEFAULT_PRESET;
};

export const deriveStoreBranding = (establishment: any): StoreBranding => {
  const settings = establishment?.settings || {};

  return {
    name: establishment?.name || '',
    logoUrl: establishment?.logoUrl || '',
    coverImageUrl: settings.coverImageUrl || '',
    tagline: typeof settings.tagline === 'string' ? settings.tagline : '',
    primaryColor: isHexColor(settings.primaryColor) ? settings.primaryColor.trim() : DEFAULT_PRIMARY,
    accentColor: isHexColor(settings.accentColor) ? settings.accentColor.trim() : DEFAULT_ACCENT,
    themePreset: normalizePreset(settings.storeThemePreset),
  };
};

const getStorageKey = (slug: string) => `gastroflow_store_branding_${slug}`;

export const cacheStoreBranding = (slug: string, branding: StoreBranding) => {
  if (typeof window === 'undefined' || !slug) return;

  try {
    window.localStorage.setItem(getStorageKey(slug), JSON.stringify(branding));
  } catch (error) {
    console.error('Failed to cache store branding', error);
  }
};

export const getCachedStoreBranding = (slug: string): StoreBranding | null => {
  if (typeof window === 'undefined' || !slug) return null;

  try {
    const raw = window.localStorage.getItem(getStorageKey(slug));
    if (!raw) return null;
    const parsed = JSON.parse(raw);

    return {
      name: typeof parsed?.name === 'string' ? parsed.name : '',
      logoUrl: typeof parsed?.logoUrl === 'string' ? parsed.logoUrl : '',
      coverImageUrl: typeof parsed?.coverImageUrl === 'string' ? parsed.coverImageUrl : '',
      tagline: typeof parsed?.tagline === 'string' ? parsed.tagline : '',
      primaryColor: isHexColor(parsed?.primaryColor) ? parsed.primaryColor.trim() : DEFAULT_PRIMARY,
      accentColor: isHexColor(parsed?.accentColor) ? parsed.accentColor.trim() : DEFAULT_ACCENT,
      themePreset: normalizePreset(parsed?.themePreset),
    };
  } catch (error) {
    console.error('Failed to read cached store branding', error);
    return null;
  }
};
